import React, { createContext, useContext, useReducer, useEffect } from 'react';

const GameContext = createContext();

const initialState = {
  level: 1,
  experience: 0,
  coins: 100,
  score: 0,
  recipes: ['espresso', 'americano', 'latte'],
  unlockedItems: ['basic_cup', 'sugar', 'milk'],
  inventory: {
    beans: 50,
    milk: 30,
    sugar: 20,
    syrups: 10,
    matcha: 0,
    ice: 0
  },
  stats: {
    drinksServed: 0,
    perfectOrders: 0,
    totalEarnings: 0,
    averageTime: 0,
    perfectMatchaOrders: 0,
    matchaDrinksServed: 0
  },
  achievements: [],
  currentDay: 1,
  cafeUpgrades: {
    espressoMachine: 1,
    grinder: 1,
    milkSteamer: 1,
    decoration: 1,
    matchaWhisk: 1,
    // Idle upgrades
    autoEspresso: 0,
    smartSteamer: 0,
    customerMagnet: 0,
    speedBoost: 0,
    qualityEnhancer: 0
  },
  // NEW: Auto Barista System
  autoBaristas: {
    // Structure: { baristaType: { count: number, efficiency: number, specialties: string[] } }
  }
};

function gameReducer(state, action) {
  switch (action.type) {
    case 'ADD_COINS':
      return { ...state, coins: state.coins + action.payload };
      
    case 'SPEND_COINS':
      return { ...state, coins: Math.max(0, state.coins - action.payload) };
      
    case 'ADD_EXPERIENCE':
      const newExp = state.experience + action.payload;
      const newLevel = Math.floor(newExp / 100) + 1;
      
      // Auto-unlock matcha recipes at level 4
      let newRecipes = [...state.recipes];
      let newUnlockedItems = [...state.unlockedItems];
      
      if (newLevel >= 4 && state.level < 4) {
        if (!newRecipes.includes('matcha_tea')) {
          newRecipes.push('matcha_tea');
        }
        if (!newUnlockedItems.includes('matcha_powder')) {
          newUnlockedItems.push('matcha_powder');
        }
        if (!newUnlockedItems.includes('ice')) {
          newUnlockedItems.push('ice');
        }
      }
      
      return {
        ...state,
        experience: newExp,
        level: Math.max(state.level, newLevel),
        recipes: newRecipes,
        unlockedItems: newUnlockedItems
      };
      
    case 'ADD_SCORE':
      return { ...state, score: Math.max(state.score, action.payload) };
      
    case 'UNLOCK_RECIPE':
      if (!state.recipes.includes(action.payload)) {
        return { ...state, recipes: [...state.recipes, action.payload] };
      }
      return state;
      
    case 'UNLOCK_ITEM':
      if (!state.unlockedItems.includes(action.payload)) {
        return { ...state, unlockedItems: [...state.unlockedItems, action.payload] };
      }
      return state;
      
    case 'UPDATE_INVENTORY':
      return { 
        ...state, 
        inventory: { ...state.inventory, ...action.payload } 
      };
      
    case 'UPDATE_STATS':
      return { 
        ...state, 
        stats: { ...state.stats, ...action.payload } 
      };
      
    case 'ADD_ACHIEVEMENT':
      if (!state.achievements.includes(action.payload)) {
        return { ...state, achievements: [...state.achievements, action.payload] };
      }
      return state;
      
    case 'UPGRADE_CAFE':
      return {
        ...state,
        cafeUpgrades: {
          ...state.cafeUpgrades,
          [action.payload.item]: action.payload.level
        }
      };

    // NEW: Auto Barista Actions
    case 'HIRE_BARISTA':
      return {
        ...state,
        autoBaristas: {
          ...state.autoBaristas,
          [action.payload.baristaId]: {
            count: action.payload.count,
            efficiency: action.payload.efficiency,
            specialties: action.payload.specialties
          }
        }
      };

    case 'FIRE_BARISTA':
      const updatedBaristas = { ...state.autoBaristas };
      if (updatedBaristas[action.payload.baristaId]) {
        updatedBaristas[action.payload.baristaId].count = Math.max(0, 
          updatedBaristas[action.payload.baristaId].count - 1
        );
        if (updatedBaristas[action.payload.baristaId].count === 0) {
          delete updatedBaristas[action.payload.baristaId];
        }
      }
      return {
        ...state,
        autoBaristas: updatedBaristas
      };

    case 'UPGRADE_BARISTA':
      return {
        ...state,
        autoBaristas: {
          ...state.autoBaristas,
          [action.payload.baristaId]: {
            ...state.autoBaristas[action.payload.baristaId],
            efficiency: action.payload.newEfficiency
          }
        }
      };
      
    case 'RESET_GAME':
      return initialState;
      
    default:
      return state;
  }
}

export function GameProvider({ children }) {
  const [state, dispatch] = useReducer(gameReducer, initialState);

  // Load from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem('baristaGameState');
    if (saved) {
      try {
        const parsedState = JSON.parse(saved);
        // Merge saved state with current state (to handle new properties)
        const mergedState = {
          ...initialState,
          ...parsedState,
          cafeUpgrades: { ...initialState.cafeUpgrades, ...parsedState.cafeUpgrades },
          autoBaristas: { ...initialState.autoBaristas, ...(parsedState.autoBaristas || {}) }
        };

        // Restore each piece of state
        Object.keys(mergedState).forEach(key => {
          if (key === 'experience') {
            dispatch({ type: 'ADD_EXPERIENCE', payload: mergedState[key] - initialState.experience });
          } else if (key === 'coins') {
            dispatch({ type: 'ADD_COINS', payload: mergedState[key] - initialState.coins });
          } else if (key === 'score') {
            dispatch({ type: 'ADD_SCORE', payload: mergedState[key] });
          } else if (key === 'recipes') {
            mergedState[key].forEach(recipe => {
              dispatch({ type: 'UNLOCK_RECIPE', payload: recipe });
            });
          } else if (key === 'unlockedItems') {
            mergedState[key].forEach(item => {
              dispatch({ type: 'UNLOCK_ITEM', payload: item });
            });
          } else if (key === 'stats') {
            dispatch({ type: 'UPDATE_STATS', payload: mergedState[key] });
          } else if (key === 'inventory') {
            dispatch({ type: 'UPDATE_INVENTORY', payload: mergedState[key] });
          } else if (key === 'cafeUpgrades') {
            // Set each upgrade individually
            Object.entries(mergedState[key]).forEach(([item, level]) => {
              if (level > initialState.cafeUpgrades[item]) {
                dispatch({ type: 'UPGRADE_CAFE', payload: { item, level } });
              }
            });
          } else if (key === 'autoBaristas') {
            // Restore barista hires
            Object.entries(mergedState[key] || {}).forEach(([baristaId, baristaData]) => {
              dispatch({ 
                type: 'HIRE_BARISTA', 
                payload: { 
                  baristaId, 
                  count: baristaData.count,
                  efficiency: baristaData.efficiency,
                  specialties: baristaData.specialties
                } 
              });
            });
          }
        });
      } catch (error) {
        console.error('Failed to load game state:', error);
      }
    }
  }, []);

  // Save to localStorage whenever state changes
  useEffect(() => {
    localStorage.setItem('baristaGameState', JSON.stringify(state));
  }, [state]);

  return (
    <GameContext.Provider value={{ state, dispatch }}>
      {children}
    </GameContext.Provider>
  );
}

export function useGame() {
  const context = useContext(GameContext);
  if (!context) {
    throw new Error('useGame must be used within a GameProvider');
  }
  return context;
}